import { Emitter } from "@core/events/emitter";
import type { IAnnotationsModel } from "@models/annotations/IAnnotationsModel";
import type { IRightPanelUIModel } from "./IRightPanelUIModel";

/** The page a Hide UI folded away, so Show UI can put it back. */
type RightPanelPage = "inspector" | "comments";

/**
 * `RightPanelUIModel` — port of the visibility half of Unity's
 * `RightPanelViewModel` (`CampfireScenes/Assets/Campfire/Runtime/ViewModels/RightPanelViewModel.cs`,
 * `ShowAllExpandableUI` / `HideAllExpandableUI` and the `m_was*Visible` pair
 * their setters maintain). See `IRightPanelUIModel` for what is and is not
 * ported, and why the page lives on `IAnnotationsModel`.
 */
export class RightPanelUIModel implements IRightPanelUIModel {
  readonly onChanged = new Emitter<boolean>();

  // Unity `m_isInspectorVisible` defaults TRUE: the editor opens with the
  // inspector up.
  private m_isOpen = true;
  /**
   * Unity's `m_wasCommentPanelVisible` / `m_wasInspectorVisible`, folded into
   * one value. Set only by a Hide UI that actually folded the rail, so a Show
   * UI never opens a rail that Hide UI found already folded (the compact shell
   * folds it on entry) — Unity's `ShowAllExpandableUI` likewise opens nothing
   * when neither flag is set.
   */
  private m_restoreOnShow: RightPanelPage | null = null;

  constructor(private readonly m_annotations: IAnnotationsModel) {
    // In Unity `IsCommentPanelVisible = true` IS the slot being shown. Here the
    // page and the slot are separate flags, so opening the page has to open the
    // slot too — otherwise a marker's Reply / Share after Hide UI lands on a
    // rail that is off screen.
    this.m_annotations.onPanelOpenChanged.on((open) => {
      if (open) this.setOpen(true);
    });
  }

  get isOpen(): boolean {
    return this.m_isOpen;
  }

  /** Unity `IsAllUIHidden` => no page of the slot is visible. */
  get isAllUIHidden(): boolean {
    return !this.m_isOpen;
  }

  setOpen(open: boolean): void {
    if (this.m_isOpen === open) return;
    this.m_isOpen = open;
    // Re-opened some other way (the comments button, the compact shell's
    // inspector toggle): there is nothing left for Show UI to restore. Unity's
    // setters clear the other page's `was` flag the moment a page opens.
    if (open) this.m_restoreOnShow = null;
    this.onChanged.emit(open);
  }

  hideAllExpandableUI(): void {
    // Already folded: keep what the Hide UI that folded it remembered. Unity's
    // `else if` chain falls through untouched in the same state.
    if (!this.m_isOpen) return;
    const page: RightPanelPage = this.m_annotations.isPanelOpen ? "comments" : "inspector";
    // Close the comments page rather than hiding it open, so the header's
    // comments button does not read as pressed over nothing.
    if (page === "comments") this.m_annotations.setPanelOpen(false);
    this.setOpen(false);
    this.m_restoreOnShow = page;
  }

  showAllExpandableUI(): void {
    const page = this.m_restoreOnShow;
    if (page === null) return;
    this.m_restoreOnShow = null;
    // Reopening the comments page opens the rail through the listener above;
    // the call below is what opens it on the inspector.
    if (page === "comments") this.m_annotations.setPanelOpen(true);
    this.setOpen(true);
  }
}
